import { usePositions } from '@/stores/usePositions';
import type { PredictionContractListContract } from '@/types/prediction.types';

export function useExchangeClose() {
  const positionsStore = usePositions();

  function getContractPositionState(contract: PredictionContractListContract) {
    return {
      hasYes: positionsStore.hasPosition(contract.conid_yes),
      hasNo: positionsStore.hasPosition(contract.conid_no),
    };
  }

  /**
   * Determines if the Yes button should show as a close action.
   * - Both-sides mode (no intent): close if user has a No position (buying Yes closes No)
   * - Single-side mode (intent='close'): close if user has any position on this contract
   */
  function isYesClose(contract: PredictionContractListContract, intent?: string): boolean {
    const { hasYes, hasNo } = getContractPositionState(contract);
    if (intent === 'close') return hasYes || hasNo;
    return hasNo;
  }

  /**
   * Determines if the No button should show as a close action.
   * - Both-sides mode (no intent): close if user has a Yes position (buying No closes Yes)
   * - Single-side mode (intent='close'): close if user has any position on this contract
   */
  function isNoClose(contract: PredictionContractListContract, intent?: string): boolean {
    const { hasYes, hasNo } = getContractPositionState(contract);
    if (intent === 'close') return hasYes || hasNo;
    return hasYes;
  }

  /**
   * Returns the ib-variant for a button based on side and close state.
   */
  function getVariant(contract: PredictionContractListContract, side: 'yes' | 'no', intent?: string): string {
    const isClose = side === 'yes' ? isYesClose(contract, intent) : isNoClose(contract, intent);
    if (isClose) return 'close_order';
    return side;
  }

  return { getContractPositionState, isYesClose, isNoClose, getVariant };
}
